import { FC, useState } from 'react';
import styles from './banner.module.scss';
import XmarkIcon from '@/assets/icons/xmark.svg?react';
import DatabaseIcon from '@/assets/icons/database.svg?react';
import QuoteLeftIcon from '@/assets/icons/quote-left.svg?react';
import ClockIcon from '@/assets/icons/clock.svg?react';
import { createPortal } from 'react-dom';
import Modal from '../Modal/modal';
import { useBanner } from './hooks/useBanner';
import { useTryDeepDive } from './hooks/useTryDeepDive';
import Button from '../Button';
import useSession from '@/hooks/useSession';
import { DEEP_DIVE_INDEX, PROMPT_APPROACHES } from '@/models/thread';

/*
 * Mounts only while the modal is open, so the banner itself doesn't follow
 * every thread update through useTryDeepDive.
 */
const TryDeepDiveButton: FC<{ onTry: () => void }> = ({ onTry }) => {
  const tryDeepDive = useTryDeepDive();
  return (
    <Button
      onClick={() => {
        tryDeepDive();
        onTry();
      }}
    >
      Try Deep Dive
    </Button>
  );
};

const Banner = () => {
  const { showBanner, onBannerDismiss, onBannerComplete } = useBanner();
  const [showBannerModal, setShowBannerModal] = useState(false);
  const { pushUserEvent } = useSession();

  return (
    <>
      {showBanner && (
        <div className={styles['banner']} data-testid="banner">
          <span>
            🤿 <strong>New:</strong> Deep Dive uses the latest HDX data and
            cites sources automatically.
            <button
              type="button"
              className={styles['banner-link']}
              onClick={() => {
                pushUserEvent('click_notification_bar_learn_more');
                setShowBannerModal(true);
              }}
            >
              What's new
            </button>
          </span>
          <button
            type="button"
            aria-label="Dismiss announcement"
            className={styles['banner-close']}
            onClick={onBannerDismiss}
          >
            <XmarkIcon className={styles['banner-icon']} aria-hidden />
          </button>
        </div>
      )}
      {createPortal(
        <Modal
          size="small"
          isOpen={showBannerModal}
          title="Deep Dive, now with HDX"
          onClose={() => setShowBannerModal(false)}
        >
          <section className={styles['banner-modal-content']}>
            <ul className={styles['banner-features']}>
              <li className={styles['banner-feature']}>
                <span className={styles['banner-feature-icon']} aria-hidden>
                  <DatabaseIcon />
                </span>
                <div>
                  <h3>Built on HDX data</h3>
                  <p>
                    Deep Dive now works directly with the Humanitarian Data
                    Exchange (HDX). Its answers draw on recent data, on any
                    humanitarian topic.
                  </p>
                </div>
              </li>
              <li className={styles['banner-feature']}>
                <span className={styles['banner-feature-icon']} aria-hidden>
                  <QuoteLeftIcon />
                </span>
                <div>
                  <h3>Citations, added automatically</h3>
                  <p>
                    Sources are now marked inline while the answer is written,
                    in Deep Dive and Quick Flight alike. There's no need to
                    request them afterwards.
                  </p>
                </div>
              </li>
            </ul>
            <p className={styles['banner-wait-time']}>
              <ClockIcon aria-hidden />
              <strong>Wait time:</strong> ~
              {PROMPT_APPROACHES[DEEP_DIVE_INDEX].timeEstimate}
            </p>
            <TryDeepDiveButton
              onTry={() => {
                setShowBannerModal(false);
                onBannerComplete();
              }}
            />
          </section>
        </Modal>,
        document.body
      )}
    </>
  );
};

export default Banner;
